import { isBoolean } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTimer } from "react-timer-hook";
import clsx from "clsx";
import { TfaMethod } from "@/core/constants/tfaMethod";
import { cn } from "@/core/helpers/cn";
import { getNextAvailableAt } from "@/core/helpers/getNextAvailableAt";
import { obfuscateEmail } from "@/core/helpers/obfuscateEmail";
import { obfuscatePhone } from "@/core/helpers/obfuscatePhone";
import Icon from "@/shared/ui/Icon";
import Button from "@/shared/ui/button/Button";
import AuthCodeInput from "@/domains/auth/components/AuthCodeInput";

// TODO: import AuthCodeInput from "@/domains/auth/components/AuthCodeInput"; - это что тут делает?

const TYPE_DATA = {
  [TfaMethod.EMAIL]: {
    title: "Email код",
    description: "Отправили код на почту",
    obfuscate: obfuscateEmail,
    codeLength: 6,
  },
  [TfaMethod.SMS]: {
    title: "SMS код",
    description: "Отправили код на телефон",
    obfuscate: obfuscatePhone,
    codeLength: 4,
  },
  [TfaMethod.AUTHENTICATOR]: {
    title: "Authenticator Код",
    description: "Перейдите в приложение Auntentificator и скопируйте код",
    obfuscate: (value: string) => value,
    codeLength: 6,
  },
};

type Props = {
  type: TfaMethod;
  address: string;
  onSubmit: () => void;
  onSendCode: () => void;
  code: string;
  error?: string | undefined;
  onCodeChange: (value: string) => void;
  pending: boolean;
  sendCodePending: boolean;
  showSubmitButton?: boolean;
  className?: string;
  isValid?: boolean;
  onResetAccess?: () => void;
  title?: string;
  description?: string;
  autoSubmit?: boolean;
  timer?: number;
};

function ValidateCode({
  address,
  type,
  onSubmit,
  code,
  error,
  onCodeChange,
  onSendCode,
  pending,
  sendCodePending,
  showSubmitButton = true,
  isValid,
  className,
  title,
  description,
  onResetAccess,
  autoSubmit = true,
  timer,
}: Props) {
  const prevCode = useRef("");
  const [showResend, setShowResend] = useState(false);
  const { minutes, seconds, restart, isRunning } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: () => {},
  });

  const typeData = useMemo(() => TYPE_DATA[type], [type]);

  useEffect(() => {
    if (autoSubmit && onSubmit) {
      // пришлось добавить реф, т.к без него бесконечный вызов onSubmit
      if (code.length === typeData.codeLength && code !== prevCode.current) {
        onSubmit();
      }
    }
    prevCode.current = code;
  }, [onSubmit, code, typeData, autoSubmit]);

  useEffect(() => {
    if (timer) {
      setShowResend(true);
      restart(getNextAvailableAt(timer));
    }
  }, [timer]);

  const handleResendCode = useCallback(() => {
    onSendCode();
    if (Number(new Date()) < Number(getNextAvailableAt(timer))) {
      restart(getNextAvailableAt(timer));
    }
  }, [onSendCode, restart, timer]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!showSubmitButton) return;
        if (code.length < typeData.codeLength) return;
        onSubmit();
      }}
      className={cn("grow flex flex-col", className)}
    >
      <div className="text-16 text-white font-medium">{title || `${typeData.title}:`}</div>

      <div className="text-15 text-white/70 leading-[1.2] mt-1 mb-2 font-medium">
        {description || (
          <>
            {typeData.description}{" "}
            <span className="text-accent">{typeData.obfuscate(address)}</span>
          </>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        <AuthCodeInput // TODO: это переписать
          value={code}
          onChange={onCodeChange}
          pending={pending}
          codeLength={typeData.codeLength}
        />
        {isBoolean(isValid) && (
          <div
            className={clsx(
              "flex items-center justify-center h-[3rem] w-[4.25rem]",
              "border border-white/10 rounded-[0.5rem]",
            )}
          >
            {isValid ? (
              <Icon name="checked" className="bg-[#84FF97] w-4" />
            ) : (
              <Icon name="close" className="bg-[#FF5858] w-3" />
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-3.5 h-5 mt-2 ">
        {onResetAccess && (
          <>
            <button
              type="button"
              className="text-white text-center w-fit underline decoration-1 underline-offset-2 hover:text-white/75 transition outline-none"
              onClick={onResetAccess}
            >
              Нет доступа?
            </button>
            <div className="w-[1px] h-3 bg-white/50" />
          </>
        )}
        {type !== TfaMethod.AUTHENTICATOR && (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            {showResend ? (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                {isRunning ? (
                  <div className="flex items-center gap-2">
                    <div className="text-white/50 w-fit ">Отправить повторно через</div>
                    <div className="text-16 text-accent font-bold">
                      <span>{String(minutes).padStart(2, "0")}</span>:
                      <span>{String(seconds).padStart(2, "0")}</span>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="text-white text-center w-fit underline decoration-1 underline-offset-2 hover:text-white/75 transition outline-none disabled:text-white/50"
                    onClick={handleResendCode}
                  >
                    Отправить повторно
                  </button>
                )}
              </>
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment

              <button
                type="button"
                className="text-white/50 text-center w-fit underline decoration-1 underline-offset-2 hover:text-white/75 transition outline-none"
                onClick={() => {
                  handleResendCode();
                  setShowResend(true);
                }}
              >
                Не пришел код?
              </button>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col grow justify-end">
        {!!error && <div className="alert alert--danger mt-2.5">*{error}</div>}
        {showSubmitButton && (
          <Button
            submit
            className="btn--md w-full mt-2.5"
            loading={pending}
            disabled={code.length < typeData.codeLength}
          >
            Подтвердить
          </Button>
        )}
      </div>
    </form>
  );
}

export default React.memo(ValidateCode);
